/**
 * demoPlayerModel.ts
 *
 * The demo player's labels and gates, as PURE functions (card D2, plan §4.7,
 * §4.8, §4.9). Two jobs, one module, no Vue and no store:
 *
 *  - `readDemoStatus()` — the hub's `GET /api/demo` body (`{ mode, clock, frame }`,
 *    D0) as a typed status, **total**: a missing field, an older shape, a null
 *    where an object belongs all answer a usable default instead of throwing.
 *    The status is read by the FAB on every app load, so a hub that answers
 *    something odd must leave the app with "demo off", never a blank screen.
 *  - `demoPlayerModel()` — that status → the words and gates a player surface
 *    paints: the date, the sim time, the speed badge, the day number, whether
 *    play/seek are offered. Kept out of the components because this is the part
 *    with arithmetic (rate presets, day numbering) and the part worth pinning
 *    with a test rather than a screenshot.
 *
 * Nothing here decides WHO may press: `canControl` arrives from the caller
 * (`isAdminRole(auth.user?.role)`, the app's own admin floor), and a viewer gets
 * the same clock with every control disabled — a read-only player, per plan §4.8.
 *
 * Time is formatted in the DEVICE's zone (`toLocaleDateString` with no
 * `timeZone`), which is what every other clock surface here does: the demo day
 * is authored on the hub's own wall clock, and the demo box's app runs on that
 * box. A laptop in another zone sees the same instant worded locally.
 */

/** The speed ladder the player offers (plan §4.2): 1×, 1 min/s … 40 min/s. */
export const DEMO_SPEED_PRESETS: readonly number[] = [1, 60, 288, 900, 2400];

/** The default speed, spelled once: one real second is 4.8 sim minutes. */
export const DEMO_DEFAULT_RATE = 288;

/** The two seasons a demo day can belong to (plan §4.6). */
export type DemoSeason = 'summer' | 'winter';

/** `GET /api/demo`'s `mode` block (`DemoModeState`, D0). */
export interface DemoPlayerMode {
  enabled: boolean;
  showFab: boolean;
  replaceAgentFab: boolean;
  scriptId: string | null;
  scriptVersion: number | null;
}

/** `GET /api/demo`'s `clock` block, as the player reads it. */
export interface DemoPlayerStatus {
  /** Demo mode is on — the one switch every demo surface gates on. */
  enabled: boolean;
  /** The simulated instant, UTC ISO ('' when the hub sent none). */
  iso: string;
  /** Sim-ms per real-ms. */
  rate: number;
  /** Whether sim time is advancing. */
  playing: boolean;
  /** 0-based script day index (`day 1` in the copy). */
  dayIndex: number;
  dayId: string | null;
  /** The script's season, when the hub names one. */
  season: DemoSeason | null;
  /** How many days the script holds, when the hub says (the `1/3` in the pill). */
  dayCount: number | null;
  /** The script's own name for the day (`Summer away`), when it sends one. */
  dayName: string | null;
}

/** The whole status the player reads. */
export interface DemoStatus {
  mode: DemoPlayerMode;
  clock: DemoPlayerStatus;
}

/** One upcoming script event, as a player surface shows it. */
export interface DemoNextEvent {
  /** The sim time it happens at, `HH:MM` as the hub authored it. */
  at: string;
  /** What happens (`Ana leaves → Office`). */
  label: string;
}

/** What a player surface paints, once the status is read. */
export interface DemoPlayerModel {
  enabled: boolean;
  playing: boolean;
  rate: number;
  /** `288×` — the speed badge. */
  speedLabel: string;
  /** Index into `DEMO_SPEED_PRESETS`, or -1 for a rate the ladder lacks. */
  rateIndex: number;
  /** Can this identity play/pause? (demo on AND the admin floor) */
  canPlay: boolean;
  /** Can it change speed? (same floor) */
  canSpeed: boolean;
  /** Can it jump days? (the same floor AND a hub that offers day seeks) */
  canSeek: boolean;
  /** 1-based day number. */
  dayNumber: number;
  dayCount: number | null;
  /** `1/3`, or null while the hub does not say how many days there are. */
  dayFraction: string | null;
  season: DemoSeason | null;
  dayName: string | null;
  /** `Tue 14 Jul` in the reader's language. */
  dateLabel: string;
  /** `08:05` in the reader's language. */
  clockLabel: string;
  /** `08:20 Ana leaves` — null when nothing is scheduled / known. */
  nextEventLabel: string | null;
}

/** How a player surface is built: who is looking, in what language. */
export interface DemoPlayerOptions {
  /** `isAdminRole(auth.user?.role)` — the demo clock is operator work. */
  canControl: boolean;
  /** `localeTag()` from `utils/formatDate.ts`. */
  locale?: string;
  /** The next script event, once the timeline endpoint exists (D8a). */
  nextEvent?: DemoNextEvent | null;
  /**
   * The hub can jump days (`POST /api/demo/playback {action:'day'}`).
   * D0 answers 400 for it, so the player ships this false until D8a — a button
   * that is offered but always refused is worse than one that says "not yet".
   */
  daySeekEnabled?: boolean;
}

/** Anything off the wire as an object, never throwing. */
function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/**
 * The one-line reason a demo request was refused, in the hub's own words
 * (`services/api.ts` attaches the body's `error`/`message` and the HTTP status
 * to its throw). `null` when nothing readable came back — the surface then says
 * its own translated line rather than painting an English guess.
 */
export function demoErrorText(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const shaped = error as { message?: unknown; status?: unknown };
  if (typeof shaped.message === 'string' && shaped.message.length > 0) return shaped.message;
  if (typeof shaped.status === 'number') return `HTTP ${shaped.status}`;
  return null;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** A season the app knows, or null — the hub may word it differently. */
export function isDemoSeason(value: unknown): value is DemoSeason {
  return value === 'summer' || value === 'winter';
}

/** The instant a `clock` block names: its ISO string, else its `simMs`, else null. */
function readInstant(clock: Record<string, unknown>, frame: Record<string, unknown>): string | null {
  const iso = text(clock.iso);
  if (iso && Number.isFinite(Date.parse(iso))) return iso;
  const simMs = finiteNumber(clock.simMs) ?? finiteNumber(frame.simMs);
  return simMs === null ? null : new Date(simMs).toISOString();
}

/** The day count: the clock's own, else the script's day list (§3.3's `script.days`). */
function readDayCount(clock: Record<string, unknown>, script: Record<string, unknown>): number | null {
  const own = finiteNumber(clock.dayCount);
  if (own !== null && own > 0) return Math.trunc(own);
  const days = script.days;
  if (Array.isArray(days) && days.length > 0) return days.length;
  const declared = finiteNumber(script.dayCount) ?? finiteNumber(script.dayTotal);
  return declared !== null && declared > 0 ? Math.trunc(declared) : null;
}

/**
 * `GET /api/demo` (and every answer of `POST /api/demo/{mode,playback}`) as the
 * status the player reads — **total**. `null` only when the body is not an
 * object at all; anything else is read field by field with a default, so a
 * partially-shaped answer still paints a clock (plan's rule for this class of
 * payload: the normaliser never throws inside a computed).
 */
export function readDemoStatus(payload: unknown): DemoStatus | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const root = payload as Record<string, unknown>;
  const mode = asRecord(root.mode);
  const clockBlock = asRecord(root.clock);
  const frame = asRecord(root.frame);
  // §3.3's scripted-day block, once D3 sends it: `day:{id,index,season,label}`.
  const day = asRecord(root.day);
  const script = asRecord(root.script);

  const dayIndex = finiteNumber(clockBlock.dayIndex) ?? finiteNumber(day.index);
  const season = isDemoSeason(clockBlock.season) ? clockBlock.season : isDemoSeason(day.season) ? day.season : null;
  const rate = finiteNumber(clockBlock.rate) ?? finiteNumber(frame.rate);

  return {
    mode: {
      enabled: mode.enabled === true,
      showFab: mode.showFab === true,
      // D0's own default: replacing the agent FAB is the demo's default, and
      // keeping both is the explicit choice (see `demoModeFromRow`).
      replaceAgentFab: mode.replaceAgentFab !== false,
      scriptId: text(mode.scriptId),
      scriptVersion: finiteNumber(mode.scriptVersion),
    },
    clock: {
      enabled: mode.enabled === true,
      iso: readInstant(clockBlock, frame) ?? '',
      rate: rate !== null && rate > 0 ? rate : 1,
      playing: clockBlock.playing === true,
      dayIndex: dayIndex !== null && dayIndex >= 0 ? Math.trunc(dayIndex) : 0,
      dayId: text(clockBlock.dayId) ?? text(day.id),
      season,
      dayCount: readDayCount(clockBlock, script),
      dayName: text(day.label) ?? text(clockBlock.label),
    },
  };
}

/**
 * One playback/mode answer as a status.
 *
 * D8a briefly answered `POST /api/demo/playback` with a wrapper
 * (`{status, seek, settle}`) while the player reads the WHOLE status — the
 * reader above is total, so the wrapper normalised to "demo off, paused" and the
 * FAB unmounted while the hub played on (owner, 2026-10-22). The hub now
 * flattens the status; this accepts both shapes so a wrapper cannot take the
 * FAB down again.
 */
export function readDemoStatusResponse(payload: unknown): DemoStatus | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const inner = asRecord((payload as Record<string, unknown>).status);
  const wrapped = inner.mode !== undefined || inner.clock !== undefined;
  return readDemoStatus(wrapped ? inner : payload);
}

/**
 * A sim instant as a reader of `locale` words it:
 * `{ dateLabel: 'Tue 14 Jul', clockLabel: '21:42' }` (en-GB; Slovene reads
 * `tor., 14. jul.`). An unreadable instant yields two empty strings rather than
 * `Invalid Date` — a clock surface says nothing before it says nonsense.
 */
export function formatSimDateTime(iso: string, locale = 'en-GB'): { dateLabel: string; clockLabel: string } {
  const at = new Date(iso);
  if (!iso || !Number.isFinite(at.getTime())) return { dateLabel: '', clockLabel: '' };
  let dateLabel = '';
  let clockLabel = '';
  try {
    dateLabel = at.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
    clockLabel = at.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    // A runtime without the locale's data must not take the clock down with it.
    return { dateLabel: at.toISOString().slice(0, 10), clockLabel: at.toISOString().slice(11, 16) };
  }
  return { dateLabel, clockLabel };
}

/** Where `rate` sits on the ladder (-1 for a rate the ladder does not list). */
export function demoRateIndex(rate: number): number {
  return DEMO_SPEED_PRESETS.indexOf(rate);
}

/**
 * One step along the speed ladder. A rate off the ladder (`1.5`, or a script's
 * own 500×) steps to the next preset PAST it, so + and − always move and always
 * land on a preset the buttons can continue from. Clamped at both ends.
 */
export function stepDemoRate(rate: number, direction: 1 | -1): number {
  const presets = DEMO_SPEED_PRESETS;
  if (direction > 0) {
    const next = presets.find((preset) => preset > rate);
    return next ?? presets[presets.length - 1];
  }
  const lower = [...presets].reverse().find((preset) => preset < rate);
  return lower ?? presets[0];
}

/** `288×` — the badge. Thousands unbundled: this is a badge, not a statistic. */
export function demoSpeedLabel(rate: number): string {
  return `${Math.round(rate)}\u00d7`;
}

/**
 * The player's labels and gates for one status.
 *
 * The gates are one idea: pressing the demo clock is admin work (the hub's
 * `POST /api/demo/playback` is `authorize("admin")`), so every control answers
 * `canControl`, and `canSeek` additionally needs a hub that can jump days.
 */
export function demoPlayerModel(status: DemoStatus, options: DemoPlayerOptions): DemoPlayerModel {
  const { clock, mode } = status;
  const locale = options.locale ?? 'en-GB';
  const { dateLabel, clockLabel } = formatSimDateTime(clock.iso, locale);
  const dayNumber = clock.dayIndex + 1;
  const canControl = options.canControl && mode.enabled;
  const nextEvent = options.nextEvent ?? null;

  return {
    enabled: mode.enabled,
    playing: clock.playing,
    rate: clock.rate,
    speedLabel: demoSpeedLabel(clock.rate),
    rateIndex: demoRateIndex(clock.rate),
    canPlay: canControl,
    canSpeed: canControl && DEMO_SPEED_PRESETS.length > 1,
    canSeek: canControl && options.daySeekEnabled === true,
    dayNumber,
    dayCount: clock.dayCount,
    dayFraction: clock.dayCount !== null && clock.dayCount > 1 ? `${dayNumber}/${clock.dayCount}` : null,
    season: clock.season,
    dayName: clock.dayName,
    dateLabel,
    clockLabel,
    nextEventLabel: nextEvent ? `${nextEvent.at} ${nextEvent.label}`.trim() : null,
  };
}

/**
 * A status with one playback action applied locally, before the hub answers
 * (the optimistic half of `playback()`, card D2). Only the two fields the
 * buttons change are touched: `playing` and `rate`. A seek or a day jump is not
 * guessed — the instant comes back from the hub or not at all, because a wing
 * position on a scripted day is exactly the lie the run log exists to avoid.
 */
export function applyDemoPlayback(
  status: DemoStatus,
  action: 'play' | 'pause' | 'speed',
  rate?: number,
): DemoStatus {
  if (action === 'play') return { ...status, clock: { ...status.clock, playing: true } };
  if (action === 'pause') return { ...status, clock: { ...status.clock, playing: false } };
  const next = typeof rate === 'number' && Number.isFinite(rate) && rate > 0 ? rate : status.clock.rate;
  return { ...status, clock: { ...status.clock, rate: next } };
}
